import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, MicOff, MonitorUp, VideoOff } from 'lucide-react';
import { Avatar, cn } from '@tutor/ui';
import type { RemotePeerMedia } from '../../hooks/useWebRTC';

export interface VideoParticipant {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  isSelf: boolean;
  stream: MediaStream | null;
  hasVideo: boolean;
  audioEnabled: boolean;
  screenSharing: boolean;
}

interface VideoTileProps {
  participant: VideoParticipant;
}

function VideoTile({ participant }: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // `srcObject` cannot be set through a React prop, so it is assigned imperatively.
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;

    if (element.srcObject !== participant.stream) {
      element.srcObject = participant.stream;
    }

    if (participant.stream) {
      // Autoplay can still be refused; there is nothing to recover to but a poster.
      void element.play().catch(() => undefined);
    }
  }, [participant.stream]);

  const showVideo = participant.hasVideo && participant.stream !== null;

  return (
    <div className="relative aspect-video w-44 shrink-0 overflow-hidden rounded-lg bg-slate-800 shadow-lg ring-1 ring-black/20 sm:w-52">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        // Never play your own microphone back to you.
        muted={participant.isSelf}
        className={cn(
          'h-full w-full object-cover',
          !showVideo && 'invisible',
          // A selfie view is mirrored; a shared screen must not be.
          participant.isSelf && !participant.screenSharing && 'scale-x-[-1]',
        )}
      />

      {!showVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Avatar name={participant.fullName} src={participant.avatarUrl} size="lg" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-white">
          {participant.fullName}
          {participant.isSelf && <span className="text-white/60"> (you)</span>}
        </span>

        {participant.screenSharing && (
          <MonitorUp className="h-3 w-3 shrink-0 text-emerald-300" aria-label="Sharing screen" />
        )}
        {!participant.audioEnabled && (
          <MicOff className="h-3 w-3 shrink-0 text-rose-300" aria-label="Microphone off" />
        )}
        {!showVideo && (
          <VideoOff className="h-3 w-3 shrink-0 text-white/50" aria-label="Camera off" />
        )}
      </div>
    </div>
  );
}

export interface VideoPanelProps {
  participants: VideoParticipant[];
  className?: string;
}

/**
 * Floating video strip over the whiteboard.
 *
 * The board is the focus of a tutoring session, so video sits on top of it as a
 * collapsible strip rather than taking a column of its own. Nothing renders at all
 * while every camera is off, so an audio-only lesson keeps the full board.
 */
export function VideoPanel({ participants, className }: VideoPanelProps) {
  const [collapsed, setCollapsed] = useState(false);

  const visible = participants.filter(
    (participant) => participant.stream !== null || participant.hasVideo,
  );

  if (visible.length === 0) return null;

  return (
    <div className={cn('pointer-events-none absolute right-4 top-4 z-20 flex flex-col items-end gap-2', className)}>
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        className="pointer-events-auto flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-slate-900"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <>
            <ChevronDown className="h-3 w-3" aria-hidden />
            Show video ({visible.length})
          </>
        ) : (
          <>
            <ChevronUp className="h-3 w-3" aria-hidden />
            Hide video
          </>
        )}
      </button>

      {!collapsed && (
        <div className="pointer-events-auto flex max-w-[calc(100vw-2rem)] flex-wrap justify-end gap-2">
          {visible.map((participant) => (
            <VideoTile key={participant.userId} participant={participant} />
          ))}
        </div>
      )}
    </div>
  );
}
